<template>
  <div>
    <v-dialog
      v-model="isVisibleProp"
      max-width="70vw"
      @keydown.enter="handleEnterKey"
      :persistent="keepTheDialogPersistent"
    >
      <v-card class="CRUDDialog">
        <v-card-title>
          <span class="headline">{{ dialogTitle }}</span>
          <slot name="additionalActionTitle"/>
        </v-card-title>

        <v-card-text>
          <v-form v-model="isValidProp" ref="form">
            <slot name="editedItem">
              <v-container>
                <!-- you should always override to show how to edit the item -->
                <span>(this is the default template)</span>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editedItem._id"
                      label="ID"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </slot>
          </v-form>
        </v-card-text>

        <v-card-actions id="tmc-base-crud-actions">
          <v-spacer></v-spacer>
          <slot name="additionalActions"/>
          <v-btn id = "crud-closing-btn"
            :class="`tmc-base-crud-${canSave ? 'cancel' : 'close'}`"
            text color="blue darken-1"
            @click="close"
            v-if="showCancelBtn"
          >{{canSave ? 'Cancel' : 'Close'}}</v-btn>
          <v-btn v-if="canApply"
            :class="`tmc-base-crud-apply`"
            text color="blue darken-1"
            @click=" (e)=>saveOnClick(e)"
          >{{ getApplyBtnLabel }}</v-btn>
          <v-btn v-if="canSave"
            :class="`tmc-base-crud-save`"
            text color="blue darken-1"
            @click="(e)=>saveOnClick(e)"
          >Save</v-btn>
          <slot name="additionalActionsAfterSave"/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiContentSave,
} from '@mdi/js';
import {
  mapGetters,
  mapMutations,
} from 'vuex';
import {
  enterBelongsToFocusedControl,
  enterPressesFocusedControl,
  flushFocusedField,
} from '../../../../util/shared/vue-global';

export default {
  name: 'CRUDDialog',
  components: {
  },
  props: {
    baseCrudKey: {
      type: String,
      default: 'default',
    },
    canSave: {
      type: Boolean,
      default: true,
    },
    canApply: {
      type: Boolean,
      default: false,
    },
    overrideApplyBtnLabel: {
      type: String,
      default: '',
    },
    overRideDialogTitle: {
      type: String,
      default: '',
    },
    showCancelBtn: {
      type: Boolean,
      default: true,
    },
    keepTheDialogPersistent: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    icons: {
      mdiContentSave,
    },
    isValidProp: false,
    isSaving: false,
  }),
  computed: {
    ...mapGetters('base/crud', [
      'item',
      'index',
      'isVisible',
    ]),
    dialogTitle() {
      if (this.canSave === false) {
        return 'Item';
      }
      if (this.overRideDialogTitle) {
        return this.overRideDialogTitle;
      }
      return this.index(this.baseCrudKey) === -1 ? 'New Item' : 'Edit Item';
    },
    isVisibleProp: {
      get() {
        return this.isVisible(this.baseCrudKey);
      },
      set(value) {
        this.setIsVisible([this.baseCrudKey, value]);
      },
    },
    editedItem: {
      get() {
        return this.item(this.baseCrudKey);
      },
      set(value) {
        this.setItem([this.baseCrudKey, value]);
      },
    },
    getApplyBtnLabel() {
      return this.overrideApplyBtnLabel || 'APPLY';
    },
  },
  methods: {
    ...mapMutations('base/crud', [
      'setIsVisible',
      'setItem',
    ]),
    validateForm() {
      return this.$refs.form.validate();
    },
    handleEnterKey(e) {
      if (e && e.repeat) return;
      // A textarea needs its newline and a v-select needs to pick the highlighted item,
      // so those keep Enter. Buttons do NOT keep it here the way they do in crud-2.0:
      // tabbing to SAVE and pressing Enter saves through this handler, because
      // saveOnClick deliberately ignores the detail=0 click a keyboard press synthesizes.
      if (enterBelongsToFocusedControl(e, { treatButtonsAsOwners: false })) return;
      // The dialog is acting on this keystroke, so nothing behind it may.
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      // The browser must not act on it either. save() is async now - it blurs the focused
      // control and waits a tick before closing - and all of that settles in microtasks
      // while the keydown is still in flight. By the time the browser gets to the default
      // action the dialog is gone and Vuetify's VDialog has restored focus to whatever
      // opened it: the list's Add (+) button. Enter on a button presses it, so the
      // keystroke that saved the receipt went straight on to open a blank new one (Tulsa
      // Mtg Tax, 9/16).
      // Standing ON a button is the exception - pressing it is the default action the
      // user asked for (Print Receipt, Save & print), and the dialog's own SAVE/APPLY
      // buttons are harmless because saveOnClick ignores that synthesized click.
      if (e && typeof e.preventDefault === 'function' && !enterPressesFocusedControl(e)) {
        e.preventDefault();
      }
      if (this.canSave) {
        this.save();
      } else if (this.canApply) {
        this.apply();
      } else {
        this.close();
      }
    },
    saveOnClick(e) {
      if (e.detail) this.handleEnterKey();
    },
    close() {
      this.isVisibleProp = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
      });
    },
    async save(withClose = true) {
      if (this.isSaving) return;
      // set before the await below so a second click cannot slip past the guard
      this.isSaving = true;
      try {
        // A focused v-combobox has not written the user's latest keystrokes into
        // editedItem yet - it only commits once the component itself is told to blur
        // (see flushFocusedField). Clicking SAVE does not do that on its own, so read
        // the record only after the flush or the typed value is silently dropped.
        await flushFocusedField(this);
        if (!this.isValidProp) {
          this.$emit('invalidItem', this.editedItem);
          this.$refs.form.validate();
        } else {
          this.$emit('upsertItem', this.editedItem);
          if (withClose) {
            this.close();
          }
        }
      } finally {
        await this.$nextTick();
        this.isSaving = false;
      }
    },
    apply() {
      return this.save(false);
    },
    // one more method saveOnEnter -> pass prop to not use default save on enter for GL lock feat
  },
};
</script>
